import Phaser from "phaser";
import type { MoveIntent } from "@/net/types";
import { rotateInputToWorld, screenToWorld, type ScreenPos } from "@/world/map/Iso";
import type { CollisionMap } from "@/world/map/CollisionMap";
import { findPath } from "@/world/map/Pathfinder";

const dirFromVec = (dx: number, dy: number): number => {
  if (dx === 0 && dy === 0) return 0;
  const angle = Math.atan2(dy, dx);
  return Math.round((angle / (Math.PI / 4) + 8) % 8);
};

/** Distance (in tiles) at which we consider the current waypoint reached. */
const ARRIVE_THRESHOLD = 0.15;

export class MoveController {
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key };
  private lastDir = 0;

  /** Remaining waypoints to walk (A* path). First element is the next target. */
  private waypoints: Array<{ x: number; y: number }> = [];

  /** Callback fired when a click-to-move target is set (screen coords for marker). */
  onClickTarget?: (worldX: number, worldY: number) => void;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly origin: ScreenPos,
    private readonly collision?: CollisionMap,
  ) {
    if (!scene.input.keyboard) {
      throw new Error("Keyboard plugin missing");
    }
    this.cursors = scene.input.keyboard.createCursorKeys();
    this.wasd = scene.input.keyboard.addKeys("W,A,S,D") as typeof this.wasd;

    scene.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      // Convert pointer (screen-space) to world-space tile coordinates.
      // pointer.worldX/worldY already account for camera scroll.
      const sx = pointer.worldX - this.origin.sx;
      const sy = pointer.worldY - this.origin.sy;
      const world = screenToWorld(sx, sy);

      // Reject clicks on blocked tiles
      if (this.collision?.isBlocked(world.x, world.y)) {
        this.waypoints = [];
        return;
      }

      // Compute A* path if we have a collision map, otherwise direct line
      if (this.collision) {
        // selfPos isn't available here, so store the raw goal.
        // Path will be computed on next sample() call with actual position.
        this._pendingGoal = { x: world.x, y: world.y };
      } else {
        this.waypoints = [{ x: world.x, y: world.y }];
      }
      this.onClickTarget?.(world.x, world.y);
    });
  }

  /** Goal waiting for the next sample() call to compute the actual A* path. */
  private _pendingGoal: { x: number; y: number } | null = null;

  /**
   * Samples the keyboard, rotates the cardinal screen-space input 45° so it
   * lines up with the iso projection (W = visual up = world NW), normalises
   * to length ≤ 1, and returns the world-space MoveIntent for the wire.
   *
   * If click-to-move waypoints are active (and no keys are held), produces a
   * normalised direction toward the next waypoint, advancing when reached.
   */
  sample(selfX?: number, selfY?: number): MoveIntent {
    const left = this.cursors.left?.isDown || this.wasd.A.isDown;
    const right = this.cursors.right?.isDown || this.wasd.D.isDown;
    const up = this.cursors.up?.isDown || this.wasd.W.isDown;
    const down = this.cursors.down?.isDown || this.wasd.S.isDown;

    const keyHeld = left || right || up || down;

    // Keyboard input cancels any active click path
    if (keyHeld) {
      this.waypoints = [];
      this._pendingGoal = null;
    }

    // Resolve pending A* path now that we have the player's position
    if (this._pendingGoal && selfX !== undefined && selfY !== undefined && this.collision) {
      const path = findPath(this.collision, selfX, selfY, this._pendingGoal.x, this._pendingGoal.y);
      this.waypoints = path ?? [];
      this._pendingGoal = null;
    }

    // --- Click-to-move (follow waypoints) ---
    if (!keyHeld && this.waypoints.length > 0 && selfX !== undefined && selfY !== undefined) {
      const wp = this.waypoints[0]!;
      const tdx = wp.x - selfX;
      const tdy = wp.y - selfY;
      const dist = Math.sqrt(tdx * tdx + tdy * tdy);

      if (dist < ARRIVE_THRESHOLD) {
        this.waypoints.shift();
        if (this.waypoints.length === 0) {
          return { dx: 0, dy: 0, dir: this.lastDir };
        }
        // Immediately steer toward next waypoint
        const next = this.waypoints[0]!;
        const ndx = next.x - selfX;
        const ndy = next.y - selfY;
        const nd = Math.sqrt(ndx * ndx + ndy * ndy);
        if (nd < 0.001) return { dx: 0, dy: 0, dir: this.lastDir };
        const dir = dirFromVec(ndx / nd, ndy / nd);
        this.lastDir = dir;
        return { dx: ndx / nd, dy: ndy / nd, dir };
      }

      const nx = tdx / dist;
      const ny = tdy / dist;
      const dir = dirFromVec(nx, ny);
      this.lastDir = dir;
      return { dx: nx, dy: ny, dir };
    }

    // --- Keyboard movement ---
    let rx = (right ? 1 : 0) - (left ? 1 : 0);
    let ry = (down ? 1 : 0) - (up ? 1 : 0);

    if (rx !== 0 && ry !== 0) {
      const inv = 1 / Math.SQRT2;
      rx *= inv;
      ry *= inv;
    }

    const { dx, dy } = rotateInputToWorld(rx, ry);
    const dir = dx === 0 && dy === 0 ? this.lastDir : dirFromVec(dx, dy);
    this.lastDir = dir;
    return { dx, dy, dir };
  }
}
